import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Camera, CalendarClock, FolderArchive, Users, ListChecks, FileSignature, MessageCircle, ShieldCheck, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/api";
import { GuestUpload } from "@/components/GuestUpload";

const useCta = () => {
  const { user } = useAuth();
  return user ? { label: "Vai alla tua area", to: "/app" } : { label: "Inizia gratis", to: "/accedi" };
};

const CtaButton = ({ testId, className = "" }) => {
  const cta = useCta();
  const cls = `rounded-full h-12 px-7 text-base active:scale-95 transition-transform ${className}`;
  return cta.to ? (
    <Button asChild className={cls} data-testid={testId}><Link to={cta.to} onClick={() => cta.to === "/accedi" && track("cta_click")}>{cta.label} <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
  ) : (
    <Button onClick={cta.onClick} className={cls} data-testid={testId}>{cta.label} <ArrowRight className="ml-2 h-4 w-4" /></Button>
  );
};

const Header = () => (
  <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b">
    <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
      <Logo />
      <nav className="hidden md:flex gap-8 text-sm text-slate-600">
        <a href="#funzioni" className="hover:text-slate-900" data-testid="nav-features-link">Funzioni</a>
        <a href="#famiglia" className="hover:text-slate-900" data-testid="nav-family-link">Famiglia</a>
        <a href="#prezzi" className="hover:text-slate-900" data-testid="nav-pricing-link">Prezzi</a>
        <a href="#faq" className="hover:text-slate-900" data-testid="nav-faq-link">FAQ</a>
        <Link to="/prova" className="hover:text-slate-900" data-testid="nav-demo-link">Prova senza account</Link>
      </nav>
      <CtaButton testId="header-cta-btn" className="h-10 px-5 text-sm" />
    </div>
  </header>
);

const DemoCard = () => (
  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }}
    className="absolute -left-6 md:-left-16 bottom-8 w-[300px] md:w-[340px] rounded-2xl border bg-white/90 backdrop-blur-xl p-5 shadow-2xl" data-testid="hero-demo-card">
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#E07A5F]">Analisi completata</span>
      <span className="text-[11px] rounded-full bg-red-50 text-red-700 px-2 py-0.5">Urgenza alta</span>
    </div>
    <p className="mt-2 font-display font-semibold text-lg">Avviso di pagamento TARI</p>
    <p className="text-sm text-slate-600">Importo: 214,00 € · Scade il 16 dicembre</p>
    <ul className="mt-3 space-y-2 text-sm">
      {["Verifica i metri quadri indicati", "Paga con pagoPA dall'app della banca", "Salva la ricevuta nell'Archivio"].map((s) => (
        <li key={s} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 text-[#1E3A8A] shrink-0" />{s}</li>
      ))}
    </ul>
    <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
      <div className="rounded-lg bg-slate-50 p-2"><p className="font-semibold">Fai da te</p><p className="text-slate-500">5 minuti, gratis</p></div>
      <div className="rounded-lg bg-slate-50 p-2"><p className="font-semibold">CAF</p><p className="text-slate-500">Non necessario</p></div>
    </div>
  </motion.div>
);

const Hero = () => (
  <section className="relative noise overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 pb-24 md:pt-24 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
      <motion.div className="lg:col-span-7 min-w-0" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="inline-flex items-center gap-2 rounded-full border bg-white px-3 py-1 text-xs text-slate-600" data-testid="hero-live-badge">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" /> Progetto live · in continuo miglioramento
        </span>
        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
          Fai una foto alla lettera.<br /><span className="text-[#1E3A8A]">Burocrazy</span> ti dice <span className="relative whitespace-nowrap">cosa fare<span className="absolute left-0 -bottom-1 h-3 w-full bg-[#E07A5F]/30 -z-10 rounded" /></span>.
        </h1>
        <p className="mt-6 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
          Bollette, cartelle, ISEE, 730, multe, pratiche dei genitori anziani. Tutte le cose semplici che faresti al CAF, spiegate passo passo, con scadenze e promemoria.
        </p>
        <GuestUpload />
        <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 items-center text-sm">
          <Link to="/prova" className="text-slate-600 hover:text-slate-900 underline underline-offset-4" data-testid="hero-demo-btn">Guarda un esempio</Link>
          <Link to="/accedi" className="text-slate-600 hover:text-slate-900 underline underline-offset-4" data-testid="hero-cta-btn">Hai già un account? Accedi</Link>
          <a href="#funzioni" className="text-slate-600 hover:text-slate-900 underline underline-offset-4" data-testid="hero-secondary-link">Scopri le funzioni</a>
        </div>
        <p className="mt-6 text-xs text-slate-500 flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> I tuoi dati sono tuoi: leggi la <Link to="/privacy" className="underline">privacy</Link>. Esporti o cancelli tutto in un clic.</p>
      </motion.div>
      <div className="lg:col-span-5 relative">
        <div className="rounded-[2rem] overflow-hidden border aspect-[4/5] max-h-[560px] ml-auto w-[85%]">
          <img src="/images/hero.jpg" alt="Persona che fotografa un documento con lo smartphone" className="h-full w-full object-cover" />
        </div>
        <DemoCard />
      </div>
    </div>
  </section>
);

const FEATURES = [
  { icon: Camera, title: "Scatta e analizza", desc: "Foto o PDF: capisce che documento è, cosa chiede, entro quando e quanto costa.", span: "md:col-span-8 lg:col-span-7", dark: true },
  { icon: CalendarClock, title: "Scadenzario globale", desc: "Tutte le scadenze di famiglia, con promemoria in app e via email.", span: "md:col-span-4 lg:col-span-5" },
  { icon: FolderArchive, title: "Archivio con ricerca", desc: "Categorie, tag e ricerca nel contenuto delle analisi.", span: "md:col-span-4 lg:col-span-4" },
  { icon: ListChecks, title: "Cosa faccio ora?", desc: "Checklist passo passo con i link ai portali ufficiali e il confronto fai da te / CAF.", span: "md:col-span-4 lg:col-span-4" },
  { icon: FileSignature, title: "Lettere e PEC", desc: "Disdette, reclami, autotutela, deleghe. Esporti in PDF e Word.", span: "md:col-span-4 lg:col-span-4" },
  { icon: MessageCircle, title: "Chiedi a Burocrazy", desc: "Una chat che risponde in italiano semplice, senza giri di parole.", span: "md:col-span-8 lg:col-span-12", accent: true },
];

const Features = () => (
  <section id="funzioni" className="max-w-7xl mx-auto px-5 md:px-8 py-24">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E07A5F]">Cosa fa</p>
    <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight max-w-2xl">Il tuo sportello CAF tascabile, aperto sempre.</h2>
    <div className="mt-12 grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
      {FEATURES.map((f, i) => (
        <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
          className={`${f.span} lift rounded-3xl border p-8 ${f.dark ? "bg-[#0B0F19] text-white border-[#0B0F19]" : f.accent ? "bg-[#E07A5F]/10 border-[#E07A5F]/30" : "bg-white"}`} data-testid={`feature-${i}`}>
          <f.icon className={`h-7 w-7 ${f.dark ? "text-[#E07A5F]" : "text-[#1E3A8A]"}`} />
          <h3 className="mt-6 text-xl font-medium">{f.title}</h3>
          <p className={`mt-2 leading-relaxed ${f.dark ? "text-slate-300" : "text-slate-600"}`}>{f.desc}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

const FamilySection = () => (
  <section id="famiglia" className="bg-white border-y">
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-24 grid md:grid-cols-2 gap-12 items-center">
      <div className="rounded-[2rem] overflow-hidden aspect-[4/3] border">
        <img src="/images/family.jpg" alt="Coppia di anziani che usa lo smartphone" className="h-full w-full object-cover object-center" />
      </div>
      <div>
        <Users className="h-8 w-8 text-[#E07A5F]" />
        <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">Pensi anche alle pratiche di mamma e papà?</h2>
        <p className="mt-4 text-slate-600 leading-relaxed">Crea un profilo per ogni familiare: documenti, scadenze, invalidità, 104, pensione. Tutto separato, tutto in un unico posto.</p>
        <ul className="mt-6 space-y-3">
          {["Scadenze filtrate per persona", "Archivio dedicato a ogni profilo", "Guide pensate per chi assiste un anziano"].map((t) => (
            <li key={t} className="flex gap-3"><Check className="h-5 w-5 text-[#1E3A8A]" />{t}</li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

const Pricing = () => (
  <section id="prezzi" className="max-w-7xl mx-auto px-5 md:px-8 py-24">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E07A5F]">Prezzi</p>
    <h2 className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight">Meno di una pratica al CAF.</h2>
    <div className="mt-12 grid md:grid-cols-2 gap-6 max-w-4xl">
      <div className="rounded-3xl border bg-white p-8" data-testid="landing-plan-free">
        <h3 className="text-xl font-medium">Free</h3>
        <p className="mt-2 text-4xl font-display font-semibold">0 €</p>
        <ul className="mt-6 space-y-3 text-sm text-slate-700">
          {["Fino a 10 documenti in archivio", "Analisi con foto e chat AI", "Scadenzario e promemoria", "Guide e lettere PEC"].map((t) => <li key={t} className="flex gap-2"><Check className="h-4 w-4 text-[#1E3A8A]" />{t}</li>)}
        </ul>
      </div>
      <div className="rounded-3xl border-2 border-[#1E3A8A] bg-[#1E3A8A] text-white p-8 relative" data-testid="landing-plan-pro">
        <span className="absolute top-6 right-6 text-xs rounded-full bg-[#E07A5F] px-3 py-1">Consigliato</span>
        <h3 className="text-xl font-medium">Pro</h3>
        <p className="mt-2 text-4xl font-display font-semibold">9,99 € <span className="text-base font-normal text-blue-200">/ mese</span></p>
        <ul className="mt-6 space-y-3 text-sm">
          {["Documenti illimitati", "Tutto il piano Free", "Più richieste AI al giorno", "Disdici quando vuoi"].map((t) => <li key={t} className="flex gap-2"><Check className="h-4 w-4 text-[#E07A5F]" />{t}</li>)}
        </ul>
      </div>
    </div>
  </section>
);

const FAQS = [
  ["Burocrazy sostituisce il CAF?", "No. Ti aiuta con le pratiche semplici e ti dice quando conviene rivolgerti a un CAF, patronato o professionista. Le indicazioni sono informative e non costituiscono consulenza fiscale o legale."],
  ["I miei documenti sono al sicuro?", "Sono accessibili solo dal tuo account, trasmessi in modo cifrato e puoi esportarli o cancellare l'account in qualsiasi momento dalle Impostazioni."],
  ["Posso gestire i documenti dei miei genitori?", "Sì, con i Profili famiglia puoi tenere separati documenti e scadenze di ogni persona."],
  ["Come funziona il pagamento?", "Il piano Pro costa 9,99 € al mese, pagamento sicuro con carta (Stripe) o PayPal. Puoi disdire quando vuoi."],
];

const Faq = () => (
  <section id="faq" className="max-w-3xl mx-auto px-5 md:px-8 pb-24">
    <h2 className="text-3xl font-semibold tracking-tight">Domande frequenti</h2>
    <Accordion type="single" collapsible className="mt-8">
      {FAQS.map(([q, a], i) => (
        <AccordionItem key={q} value={`f${i}`}>
          <AccordionTrigger className="text-left" data-testid={`faq-trigger-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-slate-600 leading-relaxed">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </section>
);

export const Footer = () => (
  <footer className="bg-[#0B0F19] text-slate-300">
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 grid md:grid-cols-3 gap-10">
      <div>
        <Logo light />
        <p className="mt-4 text-sm leading-relaxed max-w-xs">La burocrazia italiana, finalmente comprensibile. Informazioni a scopo orientativo: verifica sempre sui portali ufficiali.</p>
      </div>
      <div className="text-sm space-y-2">
        <p className="text-white font-medium">Legale</p>
        <Link to="/privacy" className="block hover:text-white" data-testid="footer-privacy-link">Informativa privacy</Link>
        <Link to="/cookie" className="block hover:text-white" data-testid="footer-cookie-link">Cookie policy</Link>
        <Link to="/termini" className="block hover:text-white" data-testid="footer-terms-link">Termini di servizio</Link>
      </div>
      <div className="text-sm">
        <p className="text-white font-medium">Pronto?</p>
        <p className="mt-2">Prova gratis, senza carta di credito.</p>
        <div className="mt-4"><CtaButton testId="footer-cta-btn" className="bg-[#E07A5F] hover:bg-[#d06a50]" /></div>
      </div>
    </div>
    <div className="border-t border-white/10 py-6 text-center text-xs text-slate-500">© {new Date().getFullYear()} Burocrazy · Macrohard1 SRL · Strada Comunale Val San Martino 111 - 10131 Torino · P.IVA 13389550016 · <a href="mailto:info@macrohard.it" className="hover:text-white" data-testid="footer-contact-email">info@macrohard.it</a></div>
  </footer>
);

export default function Landing() {
  useEffect(() => {
    if (navigator.webdriver) return undefined;
    const t = setTimeout(() => document.visibilityState === "visible" && track("landing_view"), 3000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div data-testid="landing-page">
      <Header />
      <Hero />
      <Features />
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-24">
        <div className="rounded-3xl border bg-white p-8 md:p-12 grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-1">
            <AlertTriangle className="h-8 w-8 text-[#E07A5F]" />
            <h2 className="mt-4 text-2xl font-semibold tracking-tight">Fai da te o CAF?</h2>
            <p className="mt-2 text-slate-600">Per ogni pratica ti mostriamo tempi, costi indicativi e quando vale la pena farsi aiutare.</p>
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-slate-50 p-6"><p className="font-display font-medium">ISEE · Fai da te</p><p className="text-sm text-slate-600 mt-1">Gratis · 30-60 minuti · portale INPS con SPID</p></div>
            <div className="rounded-2xl bg-slate-50 p-6"><p className="font-display font-medium">ISEE · CAF</p><p className="text-sm text-slate-600 mt-1">Di norma gratuito · consigliato per nuclei complessi</p></div>
          </div>
        </div>
      </section>
      <FamilySection />
      <Pricing />
      <Faq />
      <Footer />
    </div>
  );
}
